import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';

export interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  userId?: string;
  timestamp?: Date;
}

export interface ConversionFunnelStep {
  step: string;
  userId: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface UserBehaviorEvent {
  userId: string;
  action: string;
  context: string;
  properties: Record<string, any>;
  timestamp: Date;
}

export class AnalyticsService {
  private isInitialized = false;
  private userId: string | null = null;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Initialize analytics service
   */
  async initialize(userId?: string): Promise<void> {
    try {
      this.userId = userId || null;
      this.isInitialized = true;

      // Track initialization event
      await this.track('analytics_initialized', {
        userId: this.userId,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      });

      console.log('Analytics service initialized');
    } catch (error) {
      console.error('Failed to initialize analytics service:', error);
    }
  }

  /**
   * Track a custom event
   */
  async track(event: string, properties: Record<string, any> = {}): Promise<void> {
    try {
      if (!this.isInitialized) {
        await this.initialize();
      }

      const eventData: AnalyticsEvent = {
        event,
        properties: {
          ...properties,
          userId: this.userId,
          sessionId: this.sessionId,
          timestamp: new Date().toISOString(),
          platform: typeof window !== 'undefined' ? 'web' : 'mobile'
        },
        userId: this.userId || undefined,
        timestamp: new Date()
      };

      // Store event in database for analysis
      await this.storeAnalyticsEvent(eventData);

      // If Mixpanel is available, also send to Mixpanel
      if (typeof window !== 'undefined' && (window as any).mixpanel) {
        (window as any).mixpanel.track(event, eventData.properties);
      }

    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track conversion funnel step
   */
  async trackFunnelStep(step: string, metadata: Record<string, any> = {}): Promise<void> {
    try {
      if (!this.userId) {
        console.warn('Cannot track funnel step without user ID');
        return;
      }

      const funnelStep: ConversionFunnelStep = {
        step,
        userId: this.userId,
        timestamp: new Date(),
        metadata: {
          ...metadata,
          sessionId: this.sessionId,
          platform: typeof window !== 'undefined' ? 'web' : 'mobile'
        }
      };

      // Store funnel step in database
      await this.storeFunnelStep(funnelStep);

      // Track as regular event as well
      await this.track(`funnel_${step}`, metadata);

    } catch (error) {
      console.error('Failed to track funnel step:', error);
    }
  }

  /**
   * Track user behavior event
   */
  async trackUserBehavior(action: string, context: string, properties: Record<string, any> = {}): Promise<void> {
    try {
      if (!this.userId) {
        console.warn('Cannot track user behavior without user ID');
        return;
      }

      const behaviorEvent: UserBehaviorEvent = {
        userId: this.userId,
        action,
        context,
        properties: {
          ...properties,
          sessionId: this.sessionId,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date()
      };

      // Store behavior event
      await this.storeUserBehavior(behaviorEvent);

      // Track as event
      await this.track(`behavior_${action}`, {
        context,
        ...properties
      });

    } catch (error) {
      console.error('Failed to track user behavior:', error);
    }
  }

  /**
   * Track key monetization events
   */
  async trackMonetizationEvent(event: 'quiz_limit_reached' | 'upgrade_prompt_shown' | 'upgrade_prompt_clicked' | 'subscription_started' | 'subscription_completed' | 'subscription_cancelled', properties: Record<string, any> = {}): Promise<void> {
    await this.track(`monetization_${event}`, {
      ...properties,
      category: 'monetization'
    });
  }

  /**
   * Set user properties for analysis
   */
  async setUserProperties(properties: Record<string, any>): Promise<void> {
    try {
      if (!this.userId) {
        console.warn('Cannot set user properties without user ID');
        return;
      }

      // Store user properties in database
      const { error } = await supabase
        .from('user_analytics_properties')
        .upsert({
          user_id: this.userId,
          properties: properties,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Failed to store user properties:', error);
      }

      // Set in Mixpanel if available
      if (typeof window !== 'undefined' && (window as any).mixpanel) {
        (window as any).mixpanel.people.set(properties);
      }

    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Get conversion funnel metrics
   */
  async getFunnelMetrics(startDate?: Date, endDate?: Date): Promise<{
    appInstall: number;
    firstQuizCompleted: number;
    thirdQuizCompleted: number;
    dailyLimitReached: number;
    upgradePromptViewed: number;
    subscriptionPlanViewed: number;
    subscriptionStarted: number;
    subscriptionCompleted: number;
    conversionRate: number;
  }> {
    try {
      const { data, error } = await supabase
        .rpc('get_conversion_funnel_metrics', {
          start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: endDate?.toISOString() || new Date().toISOString()
        });

      if (error) {
        console.error('Failed to get funnel metrics:', error);
        throw error;
      }

      return data || {
        appInstall: 0,
        firstQuizCompleted: 0,
        thirdQuizCompleted: 0,
        dailyLimitReached: 0,
        upgradePromptViewed: 0,
        subscriptionPlanViewed: 0,
        subscriptionStarted: 0,
        subscriptionCompleted: 0,
        conversionRate: 0
      };

    } catch (error) {
      console.error('Failed to get funnel metrics:', error);
      throw error;
    }
  }

  /**
   * Track A/B test assignment
   */
  async trackABTest(testName: string, variant: string, properties: Record<string, any> = {}): Promise<void> {
    await this.track('ab_test_assigned', {
      testName,
      variant,
      ...properties
    });

    // Store A/B test assignment
    if (this.userId) {
      const { error } = await supabase
        .from('user_ab_tests')
        .upsert({
          user_id: this.userId,
          test_name: testName,
          variant: variant,
          assigned_at: new Date().toISOString(),
          properties: properties
        });

      if (error) {
        console.error('Failed to store A/B test assignment:', error);
      }
    }
  }

  /**
   * Private method to store analytics event in database
   */
  private async storeAnalyticsEvent(event: AnalyticsEvent): Promise<void> {
    try {
      await supabase
        .from('analytics_events')
        .insert({
          event_name: event.event,
          user_id: event.userId,
          properties: event.properties,
          timestamp: event.timestamp?.toISOString() || new Date().toISOString(),
          session_id: this.sessionId
        });
      // Analytics is fire-and-forget — errors are silently ignored
    } catch {
      // Silent — analytics should never block or pollute console
    }
  }

  /**
   * Private method to store funnel step
   */
  private async storeFunnelStep(step: ConversionFunnelStep): Promise<void> {
    try {
      await supabase
        .from('conversion_funnel_steps')
        .insert({
          step_name: step.step,
          user_id: step.userId,
          timestamp: step.timestamp.toISOString(),
          metadata: step.metadata,
          session_id: this.sessionId
        });
    } catch {
      // Silent — analytics should never block or pollute console
    }
  }

  /**
   * Private method to store user behavior
   */
  private async storeUserBehavior(behavior: UserBehaviorEvent): Promise<void> {
    try {
      await supabase
        .from('user_behavior_events')
        .insert({
          user_id: behavior.userId,
          action: behavior.action,
          context: behavior.context,
          properties: behavior.properties,
          timestamp: behavior.timestamp.toISOString(),
          session_id: this.sessionId
        });
    } catch {
      // Silent — analytics should never block or pollute console
    }
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Update user ID (when user logs in)
   */
  setUserId(userId: string): void {
    this.userId = userId;
    
    // Update Mixpanel if available
    if (typeof window !== 'undefined' && (window as any).mixpanel) {
      (window as any).mixpanel.identify(userId);
    }
  }

  /**
   * Clear user ID (when user logs out)
   */
  clearUserId(): void {
    this.userId = null;
    this.sessionId = this.generateSessionId(); // Generate new session
  }
}

// Singleton instance
export const analyticsService = new AnalyticsService();

// Default export
export default analyticsService;